import { differenceInYears } from 'date-fns';
import { GenderRangeData, UserGenderEnum } from '../types/apiGeneratedTypes';

export function isBiomarkerActive(
  biomarker: { is_active?: boolean | null }
): boolean {
  return biomarker.is_active !== false;
}

export function filterActiveBiomarkers<T extends { is_active?: boolean | null }>(
  biomarkers: T[] | null | undefined
): T[] {
  return biomarkers?.filter(isBiomarkerActive) ?? [];
}

export function formatDisabledBiomarkersPublishError(names: string[]): string {
  if (!names.length) {
    return '';
  }

  const uniqueNames = [...new Set(names)];
  const labels = uniqueNames.map((name) => `Biomarker ${name}`);

  if (labels.length === 1) {
    return (
      `${labels[0]} is disabled. ` +
      'If you want to publish the result please enable this biomarker.'
    );
  }

  if (labels.length === 2) {
    return (
      `${labels[0]} or ${labels[1]} are disabled. ` +
      'If you want to publish the result please enable these biomarkers.'
    );
  }

  return (
    `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]} are disabled. ` +
    'If you want to publish the result please enable these biomarkers.'
  );
}

interface memberDataType {
  dob?: string | null | undefined;
  gender?: UserGenderEnum | null;
  [key: string]: any;
}

export function isAgeDependentRange(
  data: any
): data is Record<string, GenderRangeData> {
  if (!data || typeof data !== 'object') return false;

  const keys = Object.keys(data);
  return keys.every((key) => {
    const val = data[key];
    return val && typeof val === 'object' && typeof val.age_range === 'string';
  });
}

export function getAgeFromDOB(dob: string): number {
  return differenceInYears(new Date(), new Date(dob));
}

export function getClosestAgeRangeEntry(
  ageRanges: Record<string, GenderRangeData>,
  age: number
): GenderRangeData {
  const parsedRanges = Object.entries(ageRanges).map(([key, value]) => {
    const [from, to] = key.split('-').map(Number);
    return { from, to, value };
  });

  const exact = parsedRanges.find((r) => age >= r.from && age <= r.to);

  if (exact) return exact.value;

  if (parsedRanges.length === 0) {
    throw new Error('No data in rangeData');
  }

  const closest = parsedRanges.reduce((prev, curr) => {
    const prevDiff = Math.min(
      Math.abs(age - prev.from),
      Math.abs(age - prev.to)
    );
    const currDiff = Math.min(
      Math.abs(age - curr.from),
      Math.abs(age - curr.to)
    );
    return currDiff < prevDiff ? curr : prev;
  });

  return closest.value;
}

export function resolveRangeData(
  biomarker: any,
  memberData: memberDataType
): GenderRangeData | null {
  if (!memberData) return null;

  const male = biomarker.male_range_data;
  const female = biomarker.female_range_data;

  const isValidRange = (range: any) =>
    range && !Array.isArray(range) && Object.keys(range).length > 0;

  let rawRangeData: any = null;

  if (memberData.gender === UserGenderEnum.Male && isValidRange(male)) {
    rawRangeData = male;
  } else if (
    memberData.gender === UserGenderEnum.Female &&
    isValidRange(female)
  ) {
    rawRangeData = female;
  } else {
    rawRangeData = isValidRange(male)
      ? male
      : isValidRange(female)
        ? female
        : null;
  }

  if (!rawRangeData) return null;

  if (isAgeDependentRange(rawRangeData)) {
    const age = getAgeFromDOB(memberData.dob!);
    return getClosestAgeRangeEntry(rawRangeData, age);
  }

  return rawRangeData;
}

export function parseNumericValue(
  raw?: string | number
): number | null | undefined {
  if (!raw) return undefined;
  if (typeof raw === 'number') return raw;

  if (typeof raw === 'string') {
    const match = raw.match(/[-+]?\d*\.?\d+/);
    if (match) {
      return parseFloat(match[0]);
    }
  }

  return null;
}

export function formatBiomarkerValueWithComparison(value: string, unit?: string): string {
  if (!value) return '';
  
  const hasComparison = /^[<>]/.test(value.trim());
  
  if (!hasComparison) {
    return unit ? `${value} ${unit}` : value;
  }
  
  const match = value.match(/^([<>])(.+)$/);
  if (!match) return unit ? `${value} ${unit}` : value;
  
  const [, symbol, numericValue] = match;
  const cleanValue = numericValue.trim();
  
  const baseValue = unit ? `${cleanValue} ${unit}` : cleanValue;
  const comparisonValue = unit ? `${symbol}${cleanValue} ${unit}` : `${symbol}${cleanValue}`;
  
  return `${baseValue} (${comparisonValue})`;
}

export function calculateSummaryStatus(
  biomarker: any,
  resultValue: string | number,
  rangeData: GenderRangeData | null,
  memberData: memberDataType | null
): string | null {
  if (biomarker.biomarker_type === 'UNITS' || !rangeData) {
    return null;
  }

  if (biomarker.biomarker_type === 'BINARY' || biomarker.name.toLowerCase().includes('anti-nuclear')) {
    return resultValue === 'POSITIVE' ? 'Positive' : 'Negative';
  }

  const parsedValue = parseNumericValue(resultValue);
  if (!parsedValue) return null;

  const parsed = Number(parsedValue);
  if (isNaN(parsed)) return null;

  let rangeDataToUse: GenderRangeData | null = null;

  if (isAgeDependentRange(rangeData)) {
    if (!memberData?.dob) return null;
    const age = getAgeFromDOB(memberData.dob);
    rangeDataToUse = getClosestAgeRangeEntry(rangeData, age);
  } else {
    rangeDataToUse = rangeData;
  }

  if (!rangeDataToUse) return null;

  if (typeof rangeDataToUse.low_from === 'number' && parsed < rangeDataToUse.low_from) {
    return 'Low';
  }

  if (
    typeof rangeDataToUse.low_from === 'number' &&
    typeof rangeDataToUse.low_to === 'number' &&
    parsed >= rangeDataToUse.low_from &&
    parsed <= rangeDataToUse.low_to
  ) {
    return 'Low';
  }

  if (
    typeof rangeDataToUse.below_optimal_from === 'number' &&
    parsed >= rangeDataToUse.below_optimal_from
  ) {
    const optimalFrom = typeof rangeDataToUse.optimal_from === 'number' ? rangeDataToUse.optimal_from : Infinity;
    if (parsed < optimalFrom) {
      return 'Below optimal';
    }
  }

  if (
    typeof rangeDataToUse.optimal_from === 'number' &&
    typeof rangeDataToUse.optimal_to === 'number' &&
    parsed >= rangeDataToUse.optimal_from &&
    parsed <= rangeDataToUse.optimal_to
  ) {
    return 'Optimal';
  }

  if (
    typeof rangeDataToUse.optimal_to === 'number' &&
    parsed > rangeDataToUse.optimal_to
  ) {
    const highFrom = typeof rangeDataToUse.high_from === 'number' ? rangeDataToUse.high_from : Infinity;
    if (parsed < highFrom) {
      return 'Above optimal';
    }
  }

  if (typeof rangeDataToUse.high_from === 'number' && parsed >= rangeDataToUse.high_from) {
    if (typeof rangeDataToUse.high_to === 'number') {
      if (parsed <= rangeDataToUse.high_to) {
        return 'High';
      }
    } else {
      return 'High';
    }
  }

  if (
    typeof rangeDataToUse.above_optimal_to === 'number' &&
    parsed > rangeDataToUse.above_optimal_to &&
    (typeof rangeDataToUse.high_from !== 'number' || rangeDataToUse.high_from === null)
  ) {
    return 'Above optimal';
  }

  // Check if value is below optimal range (even if Low/Below optimal ranges are disabled)
  if (typeof rangeDataToUse.optimal_from === 'number' && parsed < rangeDataToUse.optimal_from) {
    // If Below optimal is enabled, use it; otherwise use Low
    if (
      typeof rangeDataToUse.below_optimal_from === 'number' &&
      parsed >= rangeDataToUse.below_optimal_from
    ) {
      return 'Below optimal';
    }
    return 'Low';
  }

  // Check if value is above optimal range but not in High
  if (typeof rangeDataToUse.optimal_to === 'number' && parsed > rangeDataToUse.optimal_to) {
    // If Above optimal is enabled, use it; otherwise use High only if High range exists
    if (
      typeof rangeDataToUse.above_optimal_from === 'number' &&
      parsed >= rangeDataToUse.above_optimal_from &&
      parsed <= (rangeDataToUse.above_optimal_to || Infinity)
    ) {
      return 'Above optimal';
    }
    // Only return High if High range is actually defined
    if (typeof rangeDataToUse.high_from === 'number' && parsed >= rangeDataToUse.high_from) {
      return 'High';
    }
    return 'Above optimal';
  }

  // Default fallback - should rarely reach here
  return null;
}
